import { AgentModel } from 'common/data/agent/model';
import { PropertyReportStatsTealiumEventEnum } from 'common/module/property-report/stats/tealium-event.enum';
import { StatsTealiumCollectTypeEnum } from 'common/module/stats/tealium/collect-type.enum';
import { StatsTealiumDataLayerEventCategoryEnum } from 'common/module/stats/tealium/data-layer/event-category.enum';
import { StatsTealiumProviderInterface } from 'common/module/stats/tealium/provider.interface';

export class PropertyReportStatsTracker {
  /**
   * Constructor
   */
  constructor(private statsTealium: StatsTealiumProviderInterface) {}

  /**
   * Trigger on saved search cta clicked
   */
  public onSelectType(
    selectedUser: string,
    agentInformation: Pick<AgentModel, 'id' | 'is_trusted'>,
    propertyId: string
  ): void {
    this.statsTealium.send(
      {
        tealium_event: PropertyReportStatsTealiumEventEnum.USER_TYPE_SELECT,
        event_action: PropertyReportStatsTealiumEventEnum.USER_TYPE_SELECT,
        event_category: StatsTealiumDataLayerEventCategoryEnum.reportProperty,
        event_label: selectedUser,
        property_listing_id: propertyId,
        agent_id: agentInformation.id,
        agent_super_status: agentInformation.is_trusted ? 'true' : 'false',
        sendToGa: true,
      },
      StatsTealiumCollectTypeEnum.link
    );
  }

  public onSelectReason(
    selectedReason: string,
    agentInformation: Pick<AgentModel, 'id' | 'is_trusted'>,
    propertyId: string
  ): void {
    this.statsTealium.send(
      {
        tealium_event: PropertyReportStatsTealiumEventEnum.REASON_SELECT,
        event_action: PropertyReportStatsTealiumEventEnum.REASON_SELECT,
        event_category: StatsTealiumDataLayerEventCategoryEnum.reportProperty,
        event_label: selectedReason,
        property_listing_id: propertyId,
        agent_id: agentInformation.id,
        agent_super_status: agentInformation.is_trusted ? 'true' : 'false',
        sendToGa: true,
      },
      StatsTealiumCollectTypeEnum.link
    );
  }

  public onSendReport(
    payloadInformation: { userType: string; reasonType: string },
    agentInformation: Pick<AgentModel, 'id' | 'is_trusted'>,
    propertyId: string
  ): void {
    if (agentInformation) {
      this.statsTealium.send(
        {
          tealium_event: PropertyReportStatsTealiumEventEnum.SEND,
          event_action: PropertyReportStatsTealiumEventEnum.SEND,
          event_category: StatsTealiumDataLayerEventCategoryEnum.reportProperty,
          event_label: `${payloadInformation.userType};${payloadInformation.reasonType}`,
          property_listing_id: propertyId,
          agent_id: agentInformation.id,
          agent_super_status: agentInformation.is_trusted ? 'true' : 'false',
          sendToGa: true,
        },
        StatsTealiumCollectTypeEnum.link
      );
    }
  }

  public onToggleListingUser(
    isAgentListing: boolean,
    agentInformation: Pick<AgentModel, 'id' | 'is_trusted'>,
    propertyId: string
  ): void {
    this.statsTealium.send(
      {
        tealium_event: PropertyReportStatsTealiumEventEnum.MY_LISTING_TOGGLE_SWITCH,
        event_action: PropertyReportStatsTealiumEventEnum.MY_LISTING_TOGGLE_SWITCH,
        event_category: StatsTealiumDataLayerEventCategoryEnum.reportProperty,
        event_label: `toggle_${isAgentListing ? 'on' : 'off'}`,
        property_listing_id: propertyId,
        agent_id: agentInformation.id,
        agent_super_status: agentInformation.is_trusted ? 'true' : 'false',
        sendToGa: true,
      },
      StatsTealiumCollectTypeEnum.link
    );
  }

  public onEligibilityValidateSuccess(
    isAgentListing: boolean,
    agentInformation: Pick<AgentModel, 'id' | 'is_trusted'>,
    propertyId: string
  ): void {
    this.statsTealium.send(
      {
        tealium_event: PropertyReportStatsTealiumEventEnum.VALIDATION_SUCCESS,
        event_action: PropertyReportStatsTealiumEventEnum.VALIDATION_SUCCESS,
        event_category: StatsTealiumDataLayerEventCategoryEnum.reportProperty,
        event_label: isAgentListing ? 'my_listing_toggle_on' : 'my_listing_toggle_off',
        agent_id: agentInformation.id,
        property_listing_id: propertyId,
        agent_super_status: agentInformation.is_trusted ? 'true' : 'false',
        sendToGa: true,
      },
      StatsTealiumCollectTypeEnum.link
    );
  }

  public onEligibilityValidateFail(
    isAgentListing: boolean,
    agentInformation: Pick<AgentModel, 'id' | 'is_trusted'>,
    propertyId: string
  ): void {
    this.statsTealium.send(
      {
        tealium_event: PropertyReportStatsTealiumEventEnum.VALIDATION_FAIL,
        event_action: PropertyReportStatsTealiumEventEnum.VALIDATION_FAIL,
        event_category: StatsTealiumDataLayerEventCategoryEnum.reportProperty,
        event_label: isAgentListing ? 'my_listing_toggle_on;pf_expert_toggle_off' : 'my_listing_toggle_off',
        property_listing_id: propertyId,
        agent_id: agentInformation.id,
        agent_super_status: agentInformation.is_trusted ? 'true' : 'false',
        sendToGa: true,
      },
      StatsTealiumCollectTypeEnum.link
    );
  }

  public onUploadClaimDocumentSuccess(
    documentType: 'ownership' | 'transaction',
    agentInformation: Pick<AgentModel, 'id' | 'is_trusted'>,
    propertyId: string
  ): void {
    this.statsTealium.send(
      {
        tealium_event: PropertyReportStatsTealiumEventEnum.UPLOAD_SUCCESS,
        event_action: PropertyReportStatsTealiumEventEnum.UPLOAD_SUCCESS,
        event_category: StatsTealiumDataLayerEventCategoryEnum.reportProperty,
        event_label: documentType,
        property_listing_id: propertyId,
        agent_id: agentInformation.id,
        agent_super_status: agentInformation.is_trusted ? 'true' : 'false',
        sendToGa: true,
      },
      StatsTealiumCollectTypeEnum.link
    );
  }

  public onUploadClaimDocumentFail(
    documentType: 'ownership' | 'transaction',
    agentInformation: Pick<AgentModel, 'id' | 'is_trusted'>,
    propertyId: string
  ): void {
    this.statsTealium.send(
      {
        tealium_event: PropertyReportStatsTealiumEventEnum.UPLOAD_FAIL,
        event_action: PropertyReportStatsTealiumEventEnum.UPLOAD_FAIL,
        event_category: StatsTealiumDataLayerEventCategoryEnum.reportProperty,
        event_label: documentType,
        property_listing_id: propertyId,
        agent_id: agentInformation.id,
        agent_super_status: agentInformation.is_trusted ? 'true' : 'false',
        sendToGa: true,
      },
      StatsTealiumCollectTypeEnum.link
    );
  }

  public onCmtRequestSuccess(agentInformation: Pick<AgentModel, 'id' | 'is_trusted'>, propertyId: string): void {
    this.statsTealium.send(
      {
        tealium_event: PropertyReportStatsTealiumEventEnum.REQUEST_SUCCESS,
        event_action: PropertyReportStatsTealiumEventEnum.REQUEST_SUCCESS,
        event_category: StatsTealiumDataLayerEventCategoryEnum.reportProperty,
        event_label: '',
        property_listing_id: propertyId,
        agent_id: agentInformation.id,
        agent_super_status: agentInformation.is_trusted ? 'true' : 'false',
        sendToGa: true,
      },
      StatsTealiumCollectTypeEnum.link
    );
  }

  public onCmtRequestFail(agentInformation: Pick<AgentModel, 'id' | 'is_trusted'>, propertyId: string): void {
    this.statsTealium.send(
      {
        tealium_event: PropertyReportStatsTealiumEventEnum.REQUEST_FAIL,
        event_action: PropertyReportStatsTealiumEventEnum.REQUEST_FAIL,
        event_category: StatsTealiumDataLayerEventCategoryEnum.reportProperty,
        event_label: '',
        property_listing_id: propertyId,
        agent_id: agentInformation.id,
        agent_super_status: agentInformation.is_trusted ? 'true' : 'false',
        sendToGa: true,
      },
      StatsTealiumCollectTypeEnum.link
    );
  }
}
